import { UUID } from '@hfc/common/src/types/UUID';
import { APPLICATION_TYPES } from '@hfc/common';
import { ChallengeQuestionsUseCase } from '@/application/shared/application/ChallengeQuestionsUseCase';
import { ChallengeQuestionSettingsUseCase } from '@/application/shared/application/ChallengeQuestionSettingsUseCase';
import { SendApplicationFeedbackUseCase } from '@/application/shared/application/SendApplicationFeedbackUseCase';
import { SendAnonymousFeedbackUseCase } from '@/application/shared/application/SendAnonymousFeedbackUseCase';
import { CheckCoverageUseCase } from '@/application/shared/application/CheckCoverageUseCase';
import { ChallengeQuestion } from '@/application/shared/domain/ChallengeQuestion';
import { EnrollmentApi } from '@/application/shared/infrastructure/dataSource/EnrollmentApi';
import { CoverageResponse, SfApiResponse, Ploc, ToastConfig } from '@hfc/ui-core';
import { ChallengeType, ChallengeQuestionSettings, ChallengeQuestionFields } from '@/application/shared/domain/ChallengeQuestionSettings';
import { ChallengeQuestionResponse } from '@/application/shared/domain/ChallengeQuestionResponse';
import { FeedbackInfo } from '@/application/shared/domain/FeedbackInfo';
import { EffectiveDatesResponse } from '@/application/shared/domain/EffectiveDatesResponse';
import { GetEffectiveDatesUseCase } from '@/application/shared/application/GetEffectiveDates';
import { SaveAndExitUseCase } from '@/application/shared/application/SaveAndExitUseCase';
import { SendSelectedChannelUseCase } from '@/application/shared/application/SendSelectedChannelUseCase';
import { ChallengeQuestionsOtpUseCase } from '@/application/shared/application/ChallengeQuestionsOtpUseCase';
import { ChallengeQuestionSettingsOtpUseCase } from '@/application/shared/application/ChallengeQuestionSettingsOtpUseCase';
import { SendVerificationCodeUseCase } from '@/application/shared/application/SendVerificationCodeUseCase';
import { OtpVerificationCodeRequest } from '@/shared/domain/OtpVerificationCodeRequest';
import { GetCCAccountDataUseCase } from '@/application/shared/application/GetCCAccountDataUseCase';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';
import { CCAccountDataResponse } from '@/shared/domain/CCAccountDataResponse';
import { SettingsUseCase } from '@/shared/application/GetSettingsUseCase';
import { SettingsApi } from '@/shared/infrastructure/dataSource/SettingsApi';
import { GlobalSetting } from '@/shared/domain/GlobalSetting';

const enrollmentRepository = new EnrollmentApi();
const checkCoverage = new CheckCoverageUseCase(enrollmentRepository);
const challengeQuestions = new ChallengeQuestionsUseCase(enrollmentRepository);
const challengeQuestionSettings = new ChallengeQuestionSettingsUseCase(enrollmentRepository);
const challengeQuestionsOtp = new ChallengeQuestionsOtpUseCase(enrollmentRepository);
const challengeQuestionSettingsOtp = new ChallengeQuestionSettingsOtpUseCase(enrollmentRepository);
const sendVerificationCode = new SendVerificationCodeUseCase(enrollmentRepository);
const getCCAccountData = new GetCCAccountDataUseCase(enrollmentRepository);
const sendSelectedChannel = new SendSelectedChannelUseCase(enrollmentRepository);
const sendApplicationFeedback = new SendApplicationFeedbackUseCase(enrollmentRepository);
const sendAnonymousFeedback = new SendAnonymousFeedbackUseCase(enrollmentRepository);
const getEffectiveDates = new GetEffectiveDatesUseCase(enrollmentRepository);
const saveAndExit = new SaveAndExitUseCase(enrollmentRepository);
const settingsApi = new SettingsApi();
const getGlobalSettings = new SettingsUseCase(settingsApi);

export interface AppStoreInterface {
  customTitle: string,
  customTitleClass: string,
  customTitleSlot: 'title' | 'content',
  showFooter: boolean,
  loading: boolean,
  loadingCoverage: boolean
  toastList: Array<ToastConfig>,
  feedbackUserData: ChallengeQuestionResponse,
  globalSettings: GlobalSetting | null,
  loadingGlobalSettings: boolean,
}

const initialState: AppStoreInterface = {
  customTitle: '',
  customTitleClass: '',
  customTitleSlot: 'title',
  showFooter: true,
  loading: false,
  loadingCoverage: false,
  toastList: [] as Array<ToastConfig>,
  feedbackUserData: {} as ChallengeQuestionResponse,
  globalSettings: null,
  loadingGlobalSettings: false,
};

export class AppStore extends Ploc<AppStoreInterface> {

  public constructor () {
    super(initialState);
  }

  public async getGlobalSettings (): Promise<void> {
    this.changeState({ ...this.state, loadingGlobalSettings: true });
    const response = await getGlobalSettings.run();
    if (response.success && response.data) {
      this.changeState({ ...this.state, globalSettings: response.data });
    }
    this.changeState({ ...this.state, loadingGlobalSettings: false });
  }

  public getCustomTitle (): string {
    return this.state.customTitle;
  }

  public async updateCustomTitle (value: string): Promise<void> {
    this.changeState({ ...this.state, customTitle: value });
  }

  public async updateCustomTitleClass (value: string): Promise<void> {
    this.changeState({ ...this.state, customTitleClass: value });
  }

  public async updateCustomTitleSlot (value: 'title' | 'content'): Promise<void> {
    this.changeState({ ...this.state, customTitleSlot: value });
  }

  public async setShowFooter (value: boolean): Promise<void> {
    this.changeState({ ...this.state, showFooter: value });
  }

  public async getEffectiveDates (applicationType: APPLICATION_TYPES): Promise<SfApiResponse<Array<EffectiveDatesResponse>>> {
    this.changeState({ ...this.state, loading: true });
    const result = await getEffectiveDates.run(applicationType);
    this.changeState({ ...this.state, loading: false });

    return result;
  }

  public async checkCoverage (applicationType: APPLICATION_TYPES, zip_code: number): Promise<SfApiResponse<CoverageResponse>> {
    this.changeState({ ...this.state, loadingCoverage: true });
    const result = await checkCoverage.run([applicationType, zip_code]);
    this.changeState({ ...this.state, loadingCoverage: false });

    return result;
  }

  public async challengeQuestionsOtp (challengeQuestion: ChallengeQuestion, type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await challengeQuestionsOtp.run([challengeQuestion, type, challengeType, uuid]);
    this.changeState({ ...this.state, loading: false });

    if (response.success && response.data) {
      const challengeQuestionResponse = response.data;
      localStorage.setItem('hfc_enrollment_token', challengeQuestionResponse.token);
      if (challengeType === ChallengeType.FEEDBACK) {
        this.changeState({ ...this.state, feedbackUserData: challengeQuestionResponse });
      }
    }

    return response;
  }

  public async challengeQuestionSettingsOtp (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionSettings>> {
    this.changeState({ ...this.state, loading: true });
    const response = await challengeQuestionSettingsOtp.run([type, challengeType, uuid]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async challengeQuestions (challengeQuestion: ChallengeQuestion, type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await challengeQuestions.run([challengeQuestion, type, challengeType, uuid]);
    this.changeState({ ...this.state, loading: false });

    if (response.success && response.data) {
      const challengeQuestionResponse = response.data;
      localStorage.setItem('hfc_enrollment_token', challengeQuestionResponse.token);
      if (challengeType === ChallengeType.FEEDBACK) {
        this.changeState({ ...this.state, feedbackUserData: challengeQuestionResponse });
      }
    }

    return response;
  }

  public async challengeQuestionSettings (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<ChallengeQuestionFields>> {
    this.changeState({ ...this.state, loading: true });
    const response = await challengeQuestionSettings.run([type, challengeType, uuid]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async sendSelectedChannel (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID, channel: OtpChannelType): Promise<SfApiResponse<ChallengeQuestionSettings>> {
    this.changeState({ ...this.state, loading: true });
    const response = await sendSelectedChannel.run([type, challengeType, uuid, channel]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async sendVerificationCode (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID, verificationCodeRequest: OtpVerificationCodeRequest): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await sendVerificationCode.run([type, challengeType, uuid, verificationCodeRequest]);
    this.changeState({ ...this.state, loading: false });
    if (response.success && response.data) {
      localStorage.setItem('hfc_enrollment_token', response.data);
    }

    return response;
  }

  public async getCCAccountData (type: APPLICATION_TYPES, challengeType: ChallengeType, uuid: UUID): Promise<SfApiResponse<CCAccountDataResponse>> {
    this.changeState({ ...this.state, loading: true });
    const response = await getCCAccountData.run([type, challengeType, uuid]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async sendApplicationFeedback (feedbackInfo: FeedbackInfo, applicationType: APPLICATION_TYPES, uuid: UUID, token?: string): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await sendApplicationFeedback.run([feedbackInfo, applicationType, uuid, token]);
    this.changeState({ ...this.state, feedbackUserData: {} as ChallengeQuestionResponse, loading: false });

    return response;
  }

  public async sendAnonymousFeedback (feedbackInfo: FeedbackInfo): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await sendAnonymousFeedback.run(feedbackInfo);
    this.changeState({ ...this.state, feedbackUserData: {} as ChallengeQuestionResponse, loading: false });

    return response;
  }

  public async saveAndExit (applicationType: APPLICATION_TYPES, email: string, application: unknown, uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loading: true });
    const response = await saveAndExit.run([applicationType, email, application, uuid]);
    this.changeState({ ...this.state, loading: false });

    return response;
  }

  public async removeErrorByIndex (i:number): Promise<void> {
    this.state.toastList?.splice(i, 1);
    this.changeState({ ...this.state, toastList: this.state.toastList });
  }

  public async addToast (toast:ToastConfig): Promise<void> {
    this.state.toastList?.push({ ...toast, id: crypto.randomUUID() });
    this.changeState({ ...this.state, toastList: this.state.toastList });
  }

}
